import { Check } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/Reveal";

const COACH_IMG = "/images/jared-dugout.jpg";

const CREDENTIALS = [
  "CAA Player of the Year & Third Team All-American at Northeastern (D1)",
  "Broke Northeastern's all-time single-season home run record (21)",
  "Former pro in the San Francisco Giants organization",
  "Years in elite weightrooms, spring trainings, and pro clubhouses",
];

const ACCOLADES = [
  { src: "/images/jared-poy.jpg", caption: "CAA Player of The Year — Northeastern" },
  { src: "/images/jared-allamerican.jpg", caption: "Third Team All-American" },
];

const STATS = [
  { k: "D1", v: "College Baseball" },
  { k: "MLB", v: "Giants Organization" },
  { k: "1:1", v: "Direct Coaching" },
];

export default function AboutCoach() {
  return (
    <section className="py-20 sm:py-28 lg:py-36" data-testid="about-coach-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative" data-testid="coach-photo-frame">
              <div className="absolute -top-3 -left-3 w-16 h-16 border-t-2 border-l-2 border-[#CCFF00]" />
              <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b-2 border-r-2 border-[#CCFF00]" />
              <img
                src={COACH_IMG}
                alt="Jared Dupere — baseball action"
                className="w-full aspect-[4/5] object-cover grayscale hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute bottom-0 left-0 bg-[#0B0B0C]/90 backdrop-blur px-5 py-3 border-t border-r border-zinc-800">
                <p className="font-display uppercase text-2xl text-white leading-none">
                  Jared Dupere
                </p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#CCFF00] font-mono2 mt-1">
                  Head Coach
                </p>
              </div>
            </div>
          </Reveal>
          <div>
            <SectionHeader index="03" label="About The Coach" title="I've Lived The Grind You're In" />
            <Reveal delay={0.1}>
              <p className="text-base sm:text-lg text-zinc-300 leading-relaxed mb-6">
                I've been where you are. I played Division 1 baseball and
                continued into professional baseball in the San Francisco Giants
                organization. I know what it takes physically to compete at the
                next level — and I know how often talented players fall short
                because their bodies weren't prepared for it.
              </p>
              <p className="text-sm sm:text-base text-zinc-400 leading-relaxed mb-10">
                That's why I coach. To give serious players the same standard of
                physical preparation the pros get — strength, speed, and
                explosiveness built specifically for baseball.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <ul className="space-y-4 mb-12">
                {CREDENTIALS.map((c, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`coach-credential-${i}`}>
                    <Check className="w-5 h-5 text-[#CCFF00] shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-zinc-200">{c}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="grid grid-cols-3 border border-zinc-800 divide-x divide-zinc-800">
                {STATS.map((s, i) => (
                  <div key={i} className="p-4 sm:p-6 text-center" data-testid={`coach-stat-${i}`}>
                    <p className="font-display text-3xl sm:text-4xl text-[#CCFF00] leading-none">
                      {s.k}
                    </p>
                    <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono2 mt-2">
                      {s.v}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="grid grid-cols-2 gap-4 mt-10 items-start">
                {ACCOLADES.map((a, i) => (
                  <figure key={a.src} data-testid={`accolade-${i}`}>
                    <img
                      src={a.src}
                      alt={a.caption}
                      className="w-full h-auto border border-zinc-800 grayscale hover:grayscale-0 transition-all duration-700"
                    />
                    <figcaption className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono2 mt-2">
                      {a.caption}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.5}>
              <figure className="mt-10" data-testid="school-record-video">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#CCFF00] font-mono2 mb-2">
                  School Record Moment
                </p>
                <div className="border border-zinc-800 overflow-hidden group relative">
                  <video
                    src="/videos/school-record.mp4"
                    poster="/videos/school-record-poster.jpg"
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full aspect-video object-cover"
                  />
                </div>
                <figcaption className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono2 mt-2">
                  Breaking Northeastern's single-season HR record — No. 21 of the season, CAA Championship game
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
